import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';
import { usersApi, TutorRole } from '../services/usersService';
import { TutorListItem } from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders/TableSkeleton';
import ErrorMessage from '../components/ErrorMessage';
import { RoleEditorDialog } from '../components/RoleEditorDialog';
import { AddTutorDialog, AddTutorInput } from '../components/AddTutorDialog';

const ROLE_CHIP_COLOR: Record<
  TutorListItem['role'],
  'default' | 'primary' | 'warning'
> = {
  viewer: 'default',
  operator: 'primary',
  admin: 'warning',
};

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning';
}

function formatDate(iso: string | undefined): string {
  // formatDateSafe guards parseISO(undefined); keep the prior raw-string
  // fallback for non-empty-but-unparseable values (NC-939).
  const formatted = formatDateSafe(iso, 'dd.MM.yyyy HH:mm');
  return formatted === '—' && iso ? iso : formatted;
}

export default function UsersPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const { data: tutors, loading, error, refetch } = useApiQuery<TutorListItem[]>(
    () => usersApi.list(),
    [isAdmin],
    { enabled: isAdmin }
  );

  // NC-595: optimistic role overrides applied on top of the server snapshot.
  // Keeps the table responsive while PUT /api/tutors/{id} settles. Cleared on
  // refetch (server is the source of truth again) or on per-row rollback.
  const [overrides, setOverrides] = useState<Record<string, TutorRole>>({});
  // NC-1071: optimistic isActive overrides, cleared on refetch/rollback.
  const [activeOverrides, setActiveOverrides] = useState<Record<string, boolean>>({});
  const [editorTutor, setEditorTutor] = useState<TutorListItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // NC-1071: inactive tutors are hidden by default (the admin "ausblenden"
  // intent); the toggle reveals them so they can be reactivated.
  const [showInactive, setShowInactive] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<TutorListItem | null>(null);
  const [togglingActive, setTogglingActive] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const displayTutors = useMemo<TutorListItem[]>(
    () =>
      (tutors ?? [])
        .map((t) => ({
          ...t,
          role: overrides[t.userId] ?? t.role,
          isActive: activeOverrides[t.userId] ?? t.isActive,
        }))
        .filter((t) => showInactive || t.isActive),
    [tutors, overrides, activeOverrides, showInactive]
  );

  const inactiveCount = useMemo(
    () =>
      (tutors ?? []).filter(
        (t) => (activeOverrides[t.userId] ?? t.isActive) === false
      ).length,
    [tutors, activeOverrides]
  );

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleRefresh = async () => {
    setOverrides({});
    setActiveOverrides({});
    await refetch();
  };

  // NC-1071: deactivate (soft-delete) or reactivate. The BE requires the role,
  // so pass the current role unchanged. Optimistic update with rollback.
  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const target = deactivateTarget;
    await toggleActive(target, false);
    setDeactivateTarget(null);
  };

  const handleReactivate = async (target: TutorListItem) => {
    await toggleActive(target, true);
  };

  const toggleActive = async (target: TutorListItem, nextActive: boolean) => {
    setTogglingActive(true);
    setActiveOverrides((prev) => ({ ...prev, [target.userId]: nextActive }));
    try {
      await usersApi.setTutorActive(target.userId, target.role, nextActive);
      setSnackbar({
        open: true,
        message: nextActive
          ? `${target.displayName} wurde reaktiviert.`
          : `${target.displayName} wurde deaktiviert (Historie bleibt erhalten).`,
        severity: 'success',
      });
    } catch (err) {
      // Rollback the optimistic flag — server rejected the change.
      setActiveOverrides((prev) => {
        const next = { ...prev };
        delete next[target.userId];
        return next;
      });
      const message = err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen.';
      const isSelfDeactivate = /deactivate your own|own account/i.test(message);
      setSnackbar({
        open: true,
        message: isSelfDeactivate
          ? 'Sie koennen sich nicht selbst deaktivieren — bitten Sie einen anderen Admin.'
          : `Fehler: ${message}`,
        severity: 'error',
      });
    } finally {
      setTogglingActive(false);
    }
  };

  const handleCreateTutor = async (input: AddTutorInput) => {
    setCreating(true);
    try {
      await usersApi.createTutor(input);
      setSnackbar({
        open: true,
        message: `Tutor ${input.displayName} (${input.role}) wurde angelegt.`,
        severity: 'success',
      });
      setAddDialogOpen(false);
      setOverrides({});
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.';
      setSnackbar({
        open: true,
        message: `Fehler: ${message}`,
        severity: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSubmitRole = async (role: TutorRole) => {
    if (!editorTutor) return;
    const target = editorTutor;
    const previousRole = target.role;
    if (role === previousRole) {
      // Defensive — the dialog already disables Submit when unchanged, but
      // a programmatic submit would still no-op cleanly here.
      setEditorTutor(null);
      return;
    }

    setSaving(true);
    setOverrides((prev) => ({ ...prev, [target.userId]: role }));

    try {
      await usersApi.updateTutorRole(target.userId, role, {
        displayName: target.displayName,
      });
      setSnackbar({
        open: true,
        message: `Rolle von ${target.displayName} ist jetzt ${role}.`,
        severity: 'success',
      });
      setEditorTutor(null);
    } catch (err) {
      // Rollback the optimistic update — server rejected the change.
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[target.userId];
        return next;
      });

      const message =
        err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen.';
      const isSelfDemote = /demote yourself|self.demote/i.test(message);
      setSnackbar({
        open: true,
        message: isSelfDemote
          ? 'Sie koennen sich nicht selbst zurueckstufen — bitten Sie einen anderen Admin.'
          : `Fehler: ${message}`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Zugriff verweigert — diese Seite ist nur für Admins zugänglich.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Tutoren</Typography>
          <Typography variant="body2" color="text.secondary">
            Uebersicht aller im Portal registrierten Tutoren. Klicken Sie eine Zeile
            an, um die Rolle zu aendern.
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label={`Inaktive anzeigen${inactiveCount ? ` (${inactiveCount})` : ''}`}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={loading}
          >
            Tutor anlegen
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {loading && <TableSkeleton rows={5} columns={6} />}

      {!loading && error && (
        <ErrorMessage
          message={error}
          title="Tutoren-Liste konnte nicht geladen werden"
          onRetry={refetch}
        />
      )}

      {!loading && !error && tutors && tutors.length === 0 && (
        <Alert severity="info">
          Noch keine Tutoren registriert. Legen Sie den Benutzer zuerst in Entra ID an und
          fuegen Sie ihn dann ueber <strong>Tutor anlegen</strong> hier hinzu — Sie benoetigen
          die Object-ID aus dem Entra Admin Center.
        </Alert>
      )}

      {!loading && !error && displayTutors.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Tutoren-Tabelle">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell>Letzter Login</TableCell>
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTutors.map((t) => (
                <TableRow
                  key={t.userId}
                  hover
                  onClick={() => setEditorTutor(t)}
                  sx={{ cursor: 'pointer', opacity: t.isActive ? 1 : 0.55 }}
                >
                  <TableCell>{t.displayName}</TableCell>
                  <TableCell>{t.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.role}
                      color={ROLE_CHIP_COLOR[t.role] ?? 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.isActive ? 'aktiv' : 'inaktiv'}
                      color={t.isActive ? 'success' : 'default'}
                      variant={t.isActive ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(t.createdAt)}</TableCell>
                  <TableCell>{formatDate(t.lastLogin)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={`Rolle von ${t.displayName} bearbeiten`}>
                      <IconButton
                        size="small"
                        aria-label={`Rolle von ${t.displayName} bearbeiten`}
                        onClick={(e) => {
                          // Stop the row's onClick from firing twice.
                          e.stopPropagation();
                          setEditorTutor(t);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {t.isActive ? (
                      <Tooltip title={`${t.displayName} deaktivieren`}>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`${t.displayName} deaktivieren`}
                          disabled={togglingActive}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeactivateTarget(t);
                          }}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`${t.displayName} reaktivieren`}>
                        <IconButton
                          size="small"
                          color="success"
                          aria-label={`${t.displayName} reaktivieren`}
                          disabled={togglingActive}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReactivate(t);
                          }}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && (tutors?.length ?? 0) > 0 && displayTutors.length === 0 && (
        <Alert severity="info">
          Alle Tutoren sind deaktiviert. Aktivieren Sie „Inaktive anzeigen", um sie einzusehen und
          zu reaktivieren.
        </Alert>
      )}

      <RoleEditorDialog
        open={!!editorTutor}
        tutor={editorTutor}
        saving={saving}
        onSubmit={handleSubmitRole}
        onClose={() => setEditorTutor(null)}
      />

      <AddTutorDialog
        open={addDialogOpen}
        saving={creating}
        onSubmit={handleCreateTutor}
        onClose={() => setAddDialogOpen(false)}
      />

      {/* NC-1071: deactivate (soft-delete) confirmation */}
      <Dialog
        open={!!deactivateTarget}
        onClose={() => !togglingActive && setDeactivateTarget(null)}
      >
        <DialogTitle>Tutor deaktivieren</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deactivateTarget?.displayName}</strong> ({deactivateTarget?.email}) wirklich
            deaktivieren?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Der Zugang wird gesperrt und der Tutor aus der Standardliste ausgeblendet. Die{' '}
            <strong>Historie bleibt erhalten</strong> — bereits durchgeführte Schulungen bleiben dem
            Tutor zugeordnet. Reaktivieren ist jederzeit möglich.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={togglingActive}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            variant="contained"
            color="error"
            disabled={togglingActive}
          >
            {togglingActive ? 'Wird deaktiviert…' : 'Deaktivieren'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
